// Global Instructions Rule Applied!
//
// Generate: enqueue a period_highlights run for one scope and period. Scope
// is a fund (the fund tracker) or an LP — an investor parent with a closed
// commitment, from globalConfigs.investor_parents (the LP universe tracker,
// LP agent programme Phase 2). Quarter mode keys on (previous quarter end,
// quarter end) — the quarterly fund report's own key — so the approved set
// is what the report renders; Custom takes any date range (the performance
// deck's twelve months, for an LP). AntD default footer = [Cancel][Generate].
import React, { useMemo, useState } from 'react'
import { Modal, Radio, Select, Switch } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import type { RootState } from '../../../state_manager/store.ts'
import { previousQuarterEnd, quarterEndOptions, QUARTER_DATE_FORMAT } from '../../../core/utils/quarterEnds.ts'
import type { GenerateHighlightsPayload } from '../services/periodHighlightsService.ts'
import { FundSelect } from '../../../core/components/FundSelect.tsx'

const EMPTY_FUNDS: Array<{ id: string; name?: string | null }> = []
const EMPTY_FUND_GROUPS: Array<{ id: string; name?: string | null }> = []
const EMPTY_INVESTOR_PARENTS: Array<{ id: string; name: string }> = []

// Phase 5 adds the consolidated fund (globalConfigs.fund_groups — the
// fund_group_tracker; the scope the consolidated quarterly report reads).
type GenerateScope = 'fund' | 'fund_group' | 'lp_account'

interface GenerateHighlightsModalProps {
  open: boolean
  submitting: boolean
  onCancel: () => void
  onGenerate: (payload: GenerateHighlightsPayload) => void
  initialFundId?: string | null
  initialScope?: GenerateScope
}

export const GenerateHighlightsModal: React.FC<GenerateHighlightsModalProps> = ({
  open, submitting, onCancel, onGenerate, initialFundId = null, initialScope = 'fund'
}) => {
  const funds = useSelector((state: RootState) => (state.globalConfigs?.funds as Array<{ id: string; name?: string | null }> | undefined) ?? EMPTY_FUNDS)
  const fundGroups = useSelector((state: RootState) => (state.globalConfigs?.fund_groups as Array<{ id: string; name?: string | null }> | undefined) ?? EMPTY_FUND_GROUPS)
  const investorParents = useSelector((state: RootState) => state.globalConfigs?.investor_parents ?? EMPTY_INVESTOR_PARENTS)
  const quarterOptions = useMemo(() => quarterEndOptions(), [])
  const [scope, setScope] = useState<GenerateScope>(initialScope)
  const [fundId, setFundId] = useState<string | null>(initialFundId)
  const [fundGroupId, setFundGroupId] = useState<string | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  const [mode, setMode] = useState<'quarter' | 'custom'>('quarter')
  const [quarterEnd, setQuarterEnd] = useState<string>(quarterOptions[0]?.value ?? '')
  const [range, setRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [force, setForce] = useState(false)

  const periodStart = mode === 'quarter' ? (quarterEnd ? previousQuarterEnd(quarterEnd) : '') : (range[0]?.format(QUARTER_DATE_FORMAT) ?? '')
  const periodEnd = mode === 'quarter' ? quarterEnd : (range[1]?.format(QUARTER_DATE_FORMAT) ?? '')
  const scopeId = scope === 'fund' ? fundId : scope === 'fund_group' ? fundGroupId : accountId
  const canSubmit = Boolean(scopeId && periodStart && periodEnd && periodEnd > periodStart)

  const submit = () => {
    if (!scopeId) return
    const base = { period_start: periodStart, period_end: periodEnd, force }
    onGenerate(scope === 'fund'
      ? { scope_type: 'fund', fund_id: scopeId, ...base }
      : scope === 'fund_group'
        ? { scope_type: 'fund_group', fund_group_id: scopeId, ...base }
        : { scope_type: 'lp_account', account_id: scopeId, ...base })
  }

  return (
    <Modal
      title='Generate period highlights'
      open={open}
      onOk={submit}
      okText='Generate'
      okButtonProps={{ disabled: !canSubmit }}
      confirmLoading={submitting}
      onCancel={onCancel}
      destroyOnHidden
      width={520}
    >
      <div data-component='GenerateHighlightsModal' className='flex flex-col gap-3 text-[13px]'>
        <div>
          <div className='text-gray-600 mb-1'>Scope</div>
          <Radio.Group size='small' value={scope} onChange={(e) => setScope(e.target.value)}>
            <Radio.Button value='fund'>Fund</Radio.Button>
            <Radio.Button value='fund_group'>Consolidated fund</Radio.Button>
            <Radio.Button value='lp_account'>LP</Radio.Button>
          </Radio.Group>
        </div>
        {scope === 'fund_group' ? (
          <div>
            <div className='text-gray-600 mb-1'>Consolidated fund</div>
            <Select
              showSearch
              optionFilterProp='label'
              placeholder='Select a consolidated fund'
              style={{ width: '100%' }}
              value={fundGroupId ?? undefined}
              onChange={(v) => setFundGroupId(v)}
              options={fundGroups.map((g) => ({ value: g.id, label: g.name ?? g.id }))}
            />
          </div>
        ) : scope === 'fund' ? (
          <div>
            <div className='text-gray-600 mb-1'>Fund</div>
            <FundSelect
              placeholder='Select a fund'
              style={{ width: '100%' }}
              value={fundId ?? undefined}
              onChange={(v) => setFundId(v)}
            />
          </div>
        ) : (
          <div>
            <div className='text-gray-600 mb-1'>LP (investor parent)</div>
            <Select
              showSearch
              optionFilterProp='label'
              placeholder='Select an investor'
              style={{ width: '100%' }}
              value={accountId ?? undefined}
              onChange={(v) => setAccountId(v)}
              options={investorParents.map((p) => ({ value: p.id, label: p.name }))}
            />
          </div>
        )}
        <div>
          <div className='text-gray-600 mb-1'>Period</div>
          <Radio.Group size='small' value={mode} onChange={(e) => setMode(e.target.value)}>
            <Radio.Button value='quarter'>Quarter end</Radio.Button>
            <Radio.Button value='custom'>Custom range</Radio.Button>
          </Radio.Group>
        </div>
        {mode === 'quarter' ? (
          <Select
            style={{ width: '100%' }}
            value={quarterEnd || undefined}
            onChange={(v) => setQuarterEnd(v)}
            options={quarterOptions}
          />
        ) : (
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            format='MM/DD/YYYY'
            value={range}
            onChange={(v) => setRange((v as [dayjs.Dayjs | null, dayjs.Dayjs | null]) ?? [null, null])}
          />
        )}
        {periodStart && periodEnd && (
          <div className='text-xs text-gray-500'>
            Key: {dayjs(periodStart).format('MM/DD/YYYY')} (exclusive) → {dayjs(periodEnd).format('MM/DD/YYYY')} (inclusive).
            {mode === 'custom' && ' The start date is the prior period end, as the quarterly report keys it.'}
            {scope === 'lp_account' && mode === 'custom' && ' For the performance deck, use the deck’s own period (its start quarter end to its final quarter end).'}
          </div>
        )}
        <div className='flex items-center gap-2'>
          <Switch size='small' checked={force} onChange={setForce} />
          <span className='text-gray-600'>Regenerate company atoms even when an approved one exists</span>
        </div>
        <div className='text-xs text-gray-500'>
          {scope === 'fund'
            ? 'Portfolio companies with a live set for this period are frozen into their atoms with no model call; companies without one get an atom run; then the fund set lands — approved by policy and awaiting sign-off, or as a draft where the policy says so. Watch the Status column.'
            : scope === 'fund_group'
              ? 'Every company of the member funds with a live set for this period is frozen into its atom with no model call; the rest get an atom run (atoms are shared with the member funds’ own sets); then the consolidated set lands — the one the consolidated quarterly report renders — approved by policy and awaiting sign-off, or as a draft where the policy says so. Watch the Status column.'
              : 'Every company in the LP’s funds with a live set for this period is frozen into its atom with no model call; the rest get an atom run (atoms are shared with the fund sets); then the LP set lands, ranked by the LP’s exposure — approved by policy and awaiting sign-off, or as a draft where the policy says so. Watch the Status column.'}
        </div>
      </div>
    </Modal>
  )
}

export default GenerateHighlightsModal
